import { useSelector } from "react-redux";
import { selectUser, selectPermissionRole } from "containers/App/selectors";
import { useCreateDownload } from "../api/downloads";
import { capture } from "utils/posthog";
import { ArrowDownTrayIcon } from "@heroicons/react/24/solid";
import DateFilter from "./DateFilter";
import { toast } from "sonner";
import { t } from "copy/t";
import { getAccessibleTextColor } from "utils/helpers";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

// Required for strict parsing below — without it dayjs ignores the format
// argument entirely and falls back to its permissive parser.
dayjs.extend(customParseFormat);

/**
 * String forms a date-range boundary may arrive in.
 *
 * Only the "All Time" fallback in `GalleryPage/index.js` supplies a string
 * (`DEFAULT_OLDEST_DATE`, and `dayjs().format("YYYY-MM-DD")`); the datepicker
 * supplies `Date` objects, which never reach this list. The wall-time form is
 * carried only because the existing suite pins it as accepted. Neither reaches
 * the wire as-is: `start_date` / `end_date` are `format: date` in the OpenAPI
 * contract, and the request is normalised to `YYYY-MM-DD` below.
 */
const ACCEPTED_DATE_FORMATS = ["YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss"];

/**
 * Parses a date-range boundary, strictly when it is a string.
 *
 * Strictness is load-bearing for strings, not stylistic. Permissive
 * `dayjs(str)` rolls out-of-range components over into a different, valid
 * date — `"2024-13-45"` becomes 2025-02-14 and reports `isValid() === true` —
 * so a malformed boundary would silently become a date the user never chose
 * and the download would run against the wrong window.
 *
 * Non-strings are passed through to the permissive constructor deliberately.
 * `react-tailwindcss-datepicker` hands `onChange` real `Date` objects, and
 * `GalleryPage` stores them unmodified, so this is the ordinary path — a Date
 * is already an unambiguous instant with no components left to roll over.
 * Strict mode requires a string and rejects a Date outright, so applying it
 * here would reject every date the picker produces.
 */
const parseRangeDate = (value) =>
  typeof value === "string"
    ? dayjs(value, ACCEPTED_DATE_FORMATS, true)
    : dayjs(value);

export const validateDateRange = (start, end) => {
  if (!start || !end) {
    return { isValid: false, errorMessage: "Select both start and end dates." };
  }
  const startDate = parseRangeDate(start);
  const endDate = parseRangeDate(end);
  if (!startDate.isValid() || !endDate.isValid()) {
    return { isValid: false, errorMessage: "Dates must be valid YYYY-MM-DD values." };
  }
  if (startDate.isAfter(endDate)) {
    return { isValid: false, errorMessage: "Start date must come before end date." };
  }
  return { isValid: true, errorMessage: null };
};

const GalleryToolbar = ({
  visible,
  logo,
  filterReset,
  filters,
  filterSubmit,
  backgroundColor,
  channel,
  computedDateRange,
}) => {
  const user = useSelector(selectUser());
  const permissionRole = useSelector(selectPermissionRole());

  const { mutate: createDownload, isPending: isSubmitting } = useCreateDownload({
    onSuccess: (data) => {
      const totalAssets = data.total_assets || data.asset_count;
      const userEmail = user?.email || "your email";

      toast.success(
        t("toast.gallery.zipping", { count: totalAssets, email: userEmail }),
        {
          id: "bulk-download-initiated",
          duration: 8000, // Keep visible for 8 seconds
        }
      );

      // Fires only when the download job was actually accepted. Slug only —
      // never the user's email the toast shows.
      capture("gif_downloaded", { channel_slug: channel?.slug });
    },
    onError: (error) => {
      toast.error(error.message || t("toast.gallery.downloadFailed"), {
        id: "bulk-download-error",
      });
    },
  });

  const handleDownloadClick = () => {
    if (!channel?.id) return;

    // Use computed dates as fallback when filters are null ("All Time")
    const effectiveStart = filters.start || computedDateRange?.oldest;
    const effectiveEnd = filters.end || computedDateRange?.newest;

    const validation = validateDateRange(effectiveStart, effectiveEnd);

    if (!validation.isValid) {
      toast.error(validation.errorMessage, { id: "bulk-download-date-error" });
      return;
    }

    // Format dates to YYYY-MM-DD for the API. Parsed through the same seam as
    // the validation above, so the value sent can never be a rolled-over date
    // that validation would have rejected, and Dates from the picker normalise
    // the same way they validate.
    const startDate = parseRangeDate(effectiveStart).format("YYYY-MM-DD");
    const endDate = parseRangeDate(effectiveEnd).format("YYYY-MM-DD");

    createDownload({ startDate, endDate, channel_id: channel?.id });
  };

  const isPhotographer = permissionRole === "photographer";
  const isAuthenticated = user && (user.id || user.authToken);
  const showDownloadButton = isAuthenticated && !isPhotographer;

  // Determine button state
  const buttonText = isSubmitting ? "Initiating..." : "Download";
  const buttonDisabled = isSubmitting || !channel?.id;

  return (
    <>
      <div
        className={` top-0 w-full duration-100 transition-all flex gap-3 px-4 z-20 h-[64px] items-center justify-between sticky bg-inherit`}
      >
        {showDownloadButton && (
          <button
            onClick={handleDownloadClick}
            disabled={buttonDisabled}
            className="flex gap-[6px] items-center opacity-100"
            style={{ fontFamily: "'Geist Mono', monospace", color: getAccessibleTextColor(backgroundColor) }}
          >
            <ArrowDownTrayIcon className="w-5 h-5 text-current stroke-current stroke-[0.5]" />
            <span className="font-bold text-[12px] uppercase leading-none">
              {buttonText}
            </span>
          </button>
        )}
        <div
          className={`transition-all duration-300 z-10 ${
            visible ? "opacity-100" : "opacity-0"
          } ${showDownloadButton ? "absolute left-1/2 -translate-x-1/2" : ""}`}
        >
          {logo}
        </div>
        <DateFilter
          backgroundColor={backgroundColor}
          filterSubmit={filterSubmit}
          filterReset={filterReset}
          filters={filters}
        />
      </div>
    </>
  );
};

export default GalleryToolbar;
